import { useState, useCallback } from 'react'
import { useToast } from '@/hooks/use-toast'

export interface LuckyStreamEvent {
  type: 'start' | 'progress' | 'image_ready' | 'complete' | 'error'
  data: {
    job_id?: string
    status?: string
    message?: string
    total?: number
    completed?: number
    variation?: string
    display_name?: string
    s3_url?: string
    zip_url?: string
    error?: string
  }
}

export interface UseLuckyStreamResult {
  isStreaming: boolean
  progress: number
  totalVariations: number
  completedVariations: number
  currentVariation: string
  generatedImages: Array<{ name: string; url: string }>
  jobId: string | null
  zipUrl: string | null
  error: string | null
  startLuckyStream: (request: LuckyStreamRequest) => Promise<string | null>
}

export interface LuckyStreamRequest {
  prompt: string
  width?: number
  height?: number
  selected_variations?: string[]
}

export function useLuckyStream(): UseLuckyStreamResult {
  const [isStreaming, setIsStreaming] = useState(false)
  const [progress, setProgress] = useState(0)
  const [totalVariations, setTotalVariations] = useState(0)
  const [completedVariations, setCompletedVariations] = useState(0)
  const [currentVariation, setCurrentVariation] = useState('')
  const [generatedImages, setGeneratedImages] = useState<Array<{ name: string; url: string }>>([])
  const [jobId, setJobId] = useState<string | null>(null)
  const [zipUrl, setZipUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const startLuckyStream = useCallback(
    async (request: LuckyStreamRequest): Promise<string | null> => {
      setIsStreaming(true)
      setProgress(0)
      setTotalVariations(0)
      setCompletedVariations(0)
      setCurrentVariation('')
      setGeneratedImages([])
      setJobId(null)
      setZipUrl(null)
      setError(null)

      const token = localStorage.getItem(
        import.meta.env?.VITE_AUTH_TOKEN_KEY || 'auth_token'
      )

      if (!token) {
        setError('Não autenticado')
        setIsStreaming(false)
        return null
      }

      // Detecta URL base
      const protocol = window.location.protocol
      const hostname = window.location.hostname
      const baseURL = import.meta.env?.VITE_API_URL || `${protocol}//${hostname}:8075`

      try {
        const response = await fetch(`${baseURL}/api/v1/art-jobs/lucky/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            prompt: request.prompt,
            width: request.width || 1080,
            height: request.height || 1080,
            modo_lucky: true,
            selected_variations: request.selected_variations,
            model: 'gemini-2.5-flash-image',
          }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || `HTTP ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('Stream não suportado')
        }

        const decoder = new TextDecoder()
        let buffer = ''
        let currentEventType = ''
        let finalJobId: string | null = null

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Keep incomplete line in buffer

          for (const line of lines) {
            const trimmedLine = line.trim()

            if (trimmedLine.startsWith('event:')) {
              currentEventType = trimmedLine.replace('event:', '').trim()
            } else if (trimmedLine.startsWith('data:')) {
              const dataStr = trimmedLine.replace('data:', '').trim()
              if (dataStr) {
                try {
                  const data = JSON.parse(dataStr)

                  switch (currentEventType) {
                    case 'start':
                      console.log('[LUCKY SSE] Start:', data)
                      setJobId(data.job_id)
                      finalJobId = data.job_id
                      toast({
                        title: 'Iniciando',
                        description: 'Gerando imagens do Lucky...',
                      })
                      break

                    case 'progress':
                      console.log('[LUCKY SSE] Progress:', data)
                      if (data.total) setTotalVariations(data.total)
                      if (data.completed !== undefined) {
                        setCompletedVariations(data.completed)
                        if (data.total) {
                          setProgress(Math.round((data.completed / data.total) * 100))
                        }
                      }
                      if (data.variation) setCurrentVariation(data.variation)
                      break

                    case 'image_ready':
                      console.log('[LUCKY SSE] Image ready:', data)
                      if (data.s3_url && data.display_name) {
                        setGeneratedImages(prev => [
                          ...prev,
                          { name: data.display_name, url: data.s3_url }
                        ])
                      }
                      if (data.completed !== undefined && data.total) {
                        setCompletedVariations(data.completed)
                        setProgress(Math.round((data.completed / data.total) * 100))
                      }
                      break

                    case 'complete':
                      console.log('[LUCKY SSE] Complete:', data)
                      setProgress(100)
                      if (data.zip_url) setZipUrl(data.zip_url)
                      if (data.job_id) finalJobId = data.job_id
                      toast({
                        title: 'Sucesso!',
                        description: `${data.total || totalVariations} imagens geradas`,
                      })
                      break

                    case 'error':
                      console.error('[LUCKY SSE] Error:', data)
                      setError(data.error || 'Erro desconhecido')
                      toast({
                        title: 'Erro',
                        description: data.error || 'Erro ao gerar imagens',
                        variant: 'destructive',
                      })
                      break
                  }
                } catch (e) {
                  console.warn('[LUCKY SSE] Parse error:', e, 'data:', dataStr)
                }
              }
            }
          }
        }

        setIsStreaming(false)
        return finalJobId
      } catch (err: any) {
        console.error('[LUCKY SSE] Error:', err)
        const msg = err.message || 'Erro ao conectar com servidor'
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        setIsStreaming(false)
        return null
      }
    },
    [toast, totalVariations]
  )

  return {
    isStreaming,
    progress,
    totalVariations,
    completedVariations,
    currentVariation,
    generatedImages,
    jobId,
    zipUrl,
    error,
    startLuckyStream,
  }
}
